import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {
  bindExternalSubCategories,
  BindExternalSubCategoriesParams,
  getBoundExternalSubCategories,
  unbindExternalSubCategory,
} from '../api';
import {
  QUERY__BOUND_EXTERNAL_SUB_CATEGORIES,
  QUERY__UNMAPPED_EXTERNAL_SUB_CATEGORIES,
} from '../queryKeys';

const queryKey = (subCategoryId: string) => [
  QUERY__BOUND_EXTERNAL_SUB_CATEGORIES,
  subCategoryId,
];

const invalidateMappings = (
  queryClient: ReturnType<typeof useQueryClient>,
  subCategoryId: string,
) => {
  void queryClient.invalidateQueries({queryKey: queryKey(subCategoryId)});
  void queryClient.invalidateQueries({
    queryKey: [QUERY__UNMAPPED_EXTERNAL_SUB_CATEGORIES],
  });
};

export const useGetBoundExternalSubCategories = (subCategoryId: string) => {
  return useQuery({
    queryKey: queryKey(subCategoryId),
    queryFn: () => getBoundExternalSubCategories(subCategoryId),
    enabled: !!subCategoryId,
  });
};

export const useBindExternalSubCategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: BindExternalSubCategoriesParams) =>
      bindExternalSubCategories(params),
    onSuccess: (_data, variables) => {
      invalidateMappings(queryClient, variables.subCategoryId);
    },
  });
};

export const useUnbindExternalSubCategory = (subCategoryId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (externalSubCategoryId: string) =>
      unbindExternalSubCategory(subCategoryId, externalSubCategoryId),
    onSuccess: () => {
      invalidateMappings(queryClient, subCategoryId);
    },
  });
};
